<template>
  <div class="goal-seek" :class="statusRootClass">
    <div class="goal-seek__body">

      <!-- ─── Coluna Esquerda: Formulário ─── -->
      <div class="goal-seek__form">

        <div class="goal-seek__section">
          <div class="goal-seek__field">
            <label class="goal-seek__label">{{ $t('variavel-manipulada') }}</label>
            <select v-model="form.manipulatedMapping" class="goal-seek__select" :disabled="isRunning">
              <option value="">{{ $t('selecione-uma-variavel-manipulada') }}</option>
              <option v-for="v in maneuverVariables" :key="v.mapping" :value="v.mapping">{{ v.name }}</option>
            </select>
          </div>

          <div class="goal-seek__field">
            <label class="goal-seek__label">{{ $t('variavel-objetivo') }}</label>
            <select v-model="form.objectiveMapping" class="goal-seek__select" :disabled="isRunning">
              <option value="">{{ $t('selecione-uma-variavel-objetivo') }}</option>
              <option v-for="v in processVariables" :key="v.mapping" :value="v.mapping">{{ v.name }}</option>
            </select>
          </div>

          <div class="goal-seek__field">
            <label class="goal-seek__label">{{ $t('gs-objetivo') }}</label>
            <input v-model.number="form.objectiveValue" type="number" step="any" class="goal-seek__input" :disabled="isRunning" />
          </div>
        </div>

        <div class="goal-seek__section">
          <div class="goal-seek__row">
            <div class="goal-seek__field goal-seek__field--half">
              <label class="goal-seek__label">{{ $t('chute-inicial') }}</label>
              <input v-model.number="form.initialValue" type="number" step="any" class="goal-seek__input" :disabled="isRunning" />
            </div>
            <div class="goal-seek__field goal-seek__field--half">
              <label class="goal-seek__label">{{ $t('chute-secundario') }}</label>
              <input v-model.number="form.secondaryValue" type="number" step="any" class="goal-seek__input" :disabled="isRunning" />
            </div>
          </div>
          <div class="goal-seek__row">
            <div class="goal-seek__field goal-seek__field--half">
              <label class="goal-seek__label">{{ $t('maximo-de-iteracoes') }}</label>
              <input v-model.number="form.maxIterations" type="number" min="1" max="200" class="goal-seek__input" :disabled="isRunning" />
            </div>
            <div class="goal-seek__field goal-seek__field--half">
              <label class="goal-seek__label">{{ $t('tolerancia') }}</label>
              <input v-model.number="form.tolerance" type="number" step="0.001" min="0" class="goal-seek__input" :disabled="isRunning" />
            </div>
          </div>
        </div>

        <!-- Botão Executar / Cancelar -->
        <div class="goal-seek__actions">
          <button
            v-if="!isRunning"
            class="gs-btn gs-btn--run"
            :disabled="!canRun"
            @click="handleRun"
          >
            <i class="fa fa-play"></i> {{ $t('executar-simulacao') }}
          </button>
          <button
            v-else
            class="gs-btn gs-btn--cancel"
            @click="handleCancel"
          >
            <i class="fa fa-stop"></i> {{ $t('cancelar') }}
          </button>
        </div>

        <!-- Resultado inline na coluna esquerda -->
        <div
          v-if="resultConverged !== null && !isRunning"
          class="goal-seek__result"
          :class="resultConverged ? 'result--success' : 'result--error'"
        >
          <div class="goal-seek__result-icon">
            <i v-if="resultConverged" class="fa fa-check-circle"></i>
            <i v-else class="fa fa-times-circle"></i>
          </div>
          <div class="goal-seek__result-text">
            <div class="goal-seek__result-title">
              <template v-if="resultConverged">{{ $t('gs-convergiu', { count: iterations.length }) }}</template>
              <template v-else>{{ $t('gs-nao-convergiu', { count: iterations.length }) }}</template>
            </div>
            <div v-if="resultConverged" class="goal-seek__result-detail">
              {{ $t('valor-final') }} <strong>{{ formatNumber(resultFinalValue) }}</strong>
              &nbsp;·&nbsp;
              {{ $t('gs-tempo-total') }}: <strong>{{ formatTime(totalElapsedMs) }}</strong>
            </div>
          </div>
        </div>

        <!-- Running indicator na coluna esquerda -->
        <div v-if="isRunning" class="goal-seek__running-indicator">
          <i class="fa fa-hourglass-half gs-spin-slow"></i>
          <span>{{ $t('simulando') }}</span>
        </div>

      </div>

      <!-- ─── Coluna Direita: Log + Gráfico ─── -->
      <div class="goal-seek__log-column" :class="{ 'goal-seek__log-column--empty': iterations.length === 0 }">

        <div class="goal-seek__log">
          <div class="goal-seek__log-header">
            {{ $t('gs-log-iteracoes') }}
            <span v-if="isRunning" class="gs-running">
              <i class="fa fa-hourglass-half gs-spin-slow"></i> {{ $t('simulando') }}
            </span>
          </div>
          <div ref="logWrap" class="goal-seek__log-table-wrap">
            <table v-if="iterations.length > 0" class="goal-seek__log-table">
              <thead>
                <tr>
                  <th class="col-num">#</th>
                  <th>{{ $t('gs-entrada') }}</th>
                  <th>{{ $t('gs-saida') }}</th>
                  <th>{{ $t('gs-erro') }}</th>
                  <th>{{ $t('gs-erro-pct') }}</th>
                  <th>{{ $t('gs-tempo') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="iter in iterations"
                  :key="iter.number"
                  :class="{
                    'row--converged': iter.number === iterations.length && !isRunning && resultConverged === true,
                    'row--failed':    iter.number === iterations.length && !isRunning && resultConverged === false
                  }"
                >
                  <td class="col-num">{{ iter.number }}</td>
                  <td>{{ formatNumber(iter.inputValue) }}</td>
                  <td>{{ formatNumber(iter.outputValue) }}</td>
                  <td>{{ formatNumber(iter.error) }}</td>
                  <td class="col-muted">{{ formatPercent(iter.errorPercent) }}</td>
                  <td class="col-muted">{{ formatTime(iter.elapsedMs) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="goal-seek__log-empty">
              <i class="fa fa-arrow-circle-left"></i>
              <span>{{ isRunning ? $t('simulando') : $t('gs-clique-executar') }}</span>
            </div>
          </div>
        </div>

        <!-- Gráfico de convergência -->
        <div v-if="iterations.length >= 1" class="goal-seek__chart">
          <apexchart
            type="line"
            height="200"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>

      </div>
    </div>

    <!-- ─── Footer: Aplicar / Descartar ─── -->
    <div v-if="resultConverged !== null && !isRunning" class="goal-seek__footer">
      <button
        v-if="resultConverged"
        class="gs-btn gs-btn--apply"
        @click="handleApply"
      >
        <i class="fa fa-check"></i> {{ $t('gs-aplicar-resultado') }}
      </button>
      <button class="gs-btn gs-btn--discard" @click="handleDiscard">
        <i class="fa fa-times"></i> {{ $t('cancelar') }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import goalSeekService from '@/services/goalSeek'
import { urlMainAPI, showError, defaultSuccess } from '@/global'

export default {
  name: 'GoalSeek',

  props: ['table', 'tableResultGoal', 'nameModel'],

  inject: ['callCalculationSimulation'],

  data () {
    return {
      maneuverVariables: [],
      processVariables: [],
      form: {
        manipulatedMapping: '',
        objectiveMapping: '',
        objectiveValue: 0,
        initialValue: 0,
        secondaryValue: 0,
        maxIterations: 10,
        tolerance: 0.01
      },
      isRunning: false,
      iterations: [],
      resultConverged: null,
      resultFinalValue: null,
      resultData: null,
      abortController: null
    }
  },

  computed: {
    canRun () {
      return (
        this.form.manipulatedMapping &&
        this.form.objectiveMapping &&
        this.form.objectiveValue !== '' &&
        this.form.objectiveValue !== null
      )
    },

    totalElapsedMs () {
      return this.iterations.reduce((sum, i) => sum + (i.elapsedMs || 0), 0)
    },

    statusRootClass () {
      if (this.isRunning) return 'goal-seek--running'
      if (this.resultConverged === true) return 'goal-seek--success'
      if (this.resultConverged === false) return 'goal-seek--error'
      return ''
    },

    chartSeries () {
      const objVal = this.form.objectiveValue
      return [
        { name: this.$t('gs-entrada'), data: this.iterations.map(i => i.inputValue) },
        { name: this.$t('gs-saida'),   data: this.iterations.map(i => i.outputValue) },
        {
          name: this.$t('gs-objetivo') + ' (' + this.formatNumber(objVal) + ')',
          data: this.iterations.map(() => objVal)
        },
        { name: this.$t('gs-erro'), data: this.iterations.map(i => Math.abs(i.error)) }
      ]
    },

    chartOptions () {
      const labels = this.iterations.map(i => String(i.number))
      const self = this
      return {
        chart: {
          type: 'area',
          background: '#1a1f2e',
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500, dynamicAnimation: { enabled: true, speed: 300 } }
        },
        colors: ['#04A4A1', '#EE7528', '#fbbf24', '#ef4444'],
        stroke: { width: [3, 3, 2, 2], curve: 'smooth', dashArray: [0, 0, 8, 0] },
        markers: { size: [4, 4, 0, 3], strokeWidth: 0 },
        fill: {
          type: ['gradient', 'gradient', 'solid', 'gradient'],
          gradient: { shade: 'dark', type: 'vertical', shadeIntensity: 0.3, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95, 100] },
          opacity: [1, 1, 0, 1]
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: labels,
          labels: { style: { fontSize: '10px', colors: 'rgba(255,255,255,0.45)' } },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: [
          {
            title: { text: this.$t('gs-entrada'), style: { fontSize: '9px', color: '#04A4A1' } },
            labels: { style: { fontSize: '9px', colors: 'rgba(255,255,255,0.4)' }, formatter: v => v != null ? self.formatNumber(v) : '' }
          },
          { show: false },
          { show: false },
          {
            opposite: true,
            title: { text: this.$t('gs-erro'), style: { fontSize: '9px', color: '#ef4444' } },
            labels: { style: { fontSize: '9px', colors: 'rgba(239,68,68,0.6)' }, formatter: v => v != null ? self.formatNumber(v) : '' },
            min: 0
          }
        ],
        grid: { borderColor: 'rgba(255,255,255,0.06)', strokeDashArray: 3, xaxis: { lines: { show: false } } },
        legend: {
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: 'rgba(255,255,255,0.6)' },
          markers: { width: 10, height: 4, radius: 2 }
        },
        tooltip: {
          shared: true, intersect: false, theme: 'dark',
          x: { formatter: val => self.$t('iteracao') + ' ' + val },
          y: { formatter: v => v != null ? self.formatNumber(v) : '-' }
        }
      }
    }
  },

  created () {
    axios.get(`${urlMainAPI}/getmaneuvervariable`).then(d => { this.maneuverVariables = d.data })
    axios.get(`${urlMainAPI}/getprocessvariable`).then(d => { this.processVariables = d.data })
  },

  beforeDestroy () {
    this.cancelExecution()
  },

  methods: {
    async handleRun () {
      if (!this.form.objectiveValue && this.form.objectiveValue !== 0) this.form.objectiveValue = 0;
      if (!this.form.initialValue && this.form.initialValue !== 0) this.form.initialValue = 0;
      if (!this.form.secondaryValue && this.form.secondaryValue !== 0) this.form.secondaryValue = 0;
      if (!this.form.maxIterations && this.form.maxIterations !== 0) this.form.maxIterations = 10;
      if (!this.form.tolerance && this.form.tolerance !== 0) this.form.tolerance = 0.01;

      this.iterations = []
      this.resultConverged = null
      this.resultFinalValue = null
      this.resultData = null
      this.isRunning = true
      this.abortController = new AbortController()

      const xmlData = JSON.parse(localStorage.getItem('xml') || '{}')
      const params = {
        manipulatedVariableMapping: this.form.manipulatedMapping,
        processVariableMapping: this.form.objectiveMapping,
        objective: this.form.objectiveValue,
        initialValue: this.form.initialValue,
        secondaryValue: this.form.secondaryValue,
        maxIteractions: this.form.maxIterations,
        tolerance: this.form.tolerance,
        scenario: xmlData.scenario || '',
        scenarioResult: xmlData.scenarioResult || ''
      }

      try {
        const result = await goalSeekService.run(params, {
          onIteration: (iter) => {
            this.iterations.push(iter)
            this.$nextTick(() => {
              if (this.$refs.logWrap) {
                this.$refs.logWrap.scrollTop = this.$refs.logWrap.scrollHeight
              }
            })
          },
          signal: this.abortController.signal
        })

        if (!result) {
          showError(this.$t('nao-foi-possivel-encontrar-uma-solucao'))
        } else {
          this.resultConverged = result.converged
          this.resultFinalValue = result.finalValue
          this.resultData = result

          if (!result.converged) {
            showError(this.$t('nao-foi-possivel-encontrar-uma-solucao'))
          } else {
            defaultSuccess(this.$t('solucao-encontrada-erro') + ' ' + this.formatNumber(
              this.iterations.length > 0 ? this.iterations[this.iterations.length - 1].error : 0
            ))
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[GoalSeek] Erro:', e)
          showError(this.$t('nao-foi-possivel-encontrar-uma-solucao'))
        }
      } finally {
        this.isRunning = false
        this.abortController = null
      }
    },

    handleCancel () {
      this.cancelExecution()
    },

    cancelExecution () {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    },

    handleApply () {
      const obj = {
        scenario: this.resultData?.scenario || '',
        scenarioResult: this.resultData?.scenarioResult || '',
        table: this.table || [],
        tableResult: this.tableResultGoal || [],
        inputMapping: null,
        forceReloadInputs: true
      }
      this.callCalculationSimulation(obj, () => {})
      this.$bvModal.hide(this.nameModel)
    },

    handleDiscard () {
      this.cancelExecution()
      this.$bvModal.hide(this.nameModel)
    },

    formatNumber (val) {
      if (val === null || val === undefined) return '-'
      const num = Number(val)
      const abs = Math.abs(num)
      if (abs === 0) return '0'
      if (abs >= 100) return num.toFixed(2)
      if (abs >= 1) return num.toFixed(4)
      const digits = Math.max(4, Math.ceil(-Math.log10(abs)) + 2)
      return num.toFixed(Math.min(digits, 8))
    },

    formatPercent (val) {
      if (val === null || val === undefined) return '-'
      const num = Number(val)
      if (num < 0.01 && num > 0) return num.toExponential(1) + '%'
      return num.toFixed(2) + '%'
    },

    formatTime (ms) {
      if (ms === null || ms === undefined) return '-'
      if (ms < 1000) return ms + 'ms'
      return (ms / 1000).toFixed(1) + 's'
    }
  }
}
</script>

<style scoped>
/* ── Root ── */
.goal-seek {
  width: 100%;
  position: relative;
}

/* Borda superior colorida que muda com o estado */
.goal-seek::before {
  content: '';
  display: block;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: #EE7528;
  margin-bottom: 12px;
  transition: background 0.35s;
}
.goal-seek--running::before  { background: #EE7528; }
.goal-seek--success::before  { background: #04A4A1; }
.goal-seek--error::before    { background: #D11E1D; }

/* ── Layout principal ── */
.goal-seek__body {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 20px;
  min-height: 420px;
}

/* ── Formulário (coluna esquerda) ── */
.goal-seek__form { display: flex; flex-direction: column; gap: 4px; }

.goal-seek__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 10px;
}
.goal-seek__section + .goal-seek__section {
  padding-top: 10px;
  border-top: 1px solid #CFD2D2;
}

.goal-seek__field  { display: flex; flex-direction: column; gap: 3px; }
.goal-seek__field--half { flex: 1; }
.goal-seek__row    { display: flex; gap: 8px; }

.goal-seek__label {
  font-size: 11px;
  font-weight: 600;
  color: #8D8F92;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.goal-seek__select,
.goal-seek__input {
  width: 100%;
  padding: 6px 9px;
  border: 1px solid #EE7528;
  border-radius: 5px;
  font-size: 12px;
  color: #232932;
  background: #fff;
  transition: all 0.15s;
  box-sizing: border-box;
}
.goal-seek__select:focus,
.goal-seek__input:focus {
  outline: none;
  border-color: #d66820;
  box-shadow: 0 0 0 2px rgba(238, 117, 40, 0.18);
}
.goal-seek__select:disabled,
.goal-seek__input:disabled {
  background: #F4F4F4;
  opacity: 0.65;
  cursor: not-allowed;
}

.goal-seek__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.goal-seek__input-wrap .goal-seek__input { padding-right: 34px; }

.goal-seek__input-unit {
  position: absolute;
  right: 8px;
  font-size: 10px;
  font-weight: 600;
  color: #8D8F92;
  pointer-events: none;
}

.goal-seek__actions {
  padding-top: 4px;
  display: flex;
  justify-content: center;
}

/* Status com ícone + duas linhas */
.goal-seek__result {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 7px;
  font-size: 12px;
}
.goal-seek__result.result--success {
  background: rgba(4, 164, 161, 0.08);
  color: #0A7479;
  border: 1px solid rgba(4, 164, 161, 0.2);
}
.goal-seek__result.result--error {
  background: rgba(209, 30, 29, 0.06);
  color: #a51716;
  border: 1px solid rgba(209, 30, 29, 0.15);
}
.goal-seek__result-icon {
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 1px;
}
.goal-seek__result-text { display: flex; flex-direction: column; gap: 2px; }
.goal-seek__result-title { font-weight: 600; font-size: 12px; }
.goal-seek__result-detail { font-size: 11px; opacity: 0.8; }
.goal-seek__result-detail strong { font-weight: 700; }

/* Indicador de execução */
.goal-seek__running-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 7px;
  background: rgba(238, 117, 40, 0.07);
  border: 1px solid rgba(238, 117, 40, 0.2);
  color: #EE7528;
  font-size: 12px;
  font-weight: 500;
}

/* ── Coluna Direita ── */
.goal-seek__log-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: opacity 0.2s;
  min-height: 0;
}
.goal-seek__log-column--empty { opacity: 0.55; }

.goal-seek__log {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #CFD2D2;
  border-radius: 6px;
  overflow: hidden;
}

.goal-seek__log-header {
  padding: 7px 12px;
  background: #232932;
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.goal-seek__log-table-wrap {
  flex: 1;
  min-height: 150px;
  max-height: 220px;
  overflow-y: auto;
  background: white;
}
.goal-seek__log-table-wrap::-webkit-scrollbar { width: 5px; }
.goal-seek__log-table-wrap::-webkit-scrollbar-thumb { background: #04A4A1; border-radius: 3px; }

.goal-seek__log-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 36px 16px;
  color: #8D8F92;
  font-size: 12px;
}
.goal-seek__log-empty .fa {
  font-size: 22px;
  opacity: 0.4;
}

.goal-seek__log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.goal-seek__log-table th {
  padding: 5px 8px;
  font-weight: 600;
  font-size: 10px;
  color: #545454;
  background: #F4F4F4;
  border-bottom: 1px solid #CFD2D2;
  position: sticky;
  top: 0;
  text-align: right;
  white-space: nowrap;
}
.goal-seek__log-table td {
  padding: 4px 8px;
  color: #545454;
  font-family: 'Cascadia Code', 'Consolas', monospace;
  font-size: 11px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid rgba(207, 210, 210, 0.4);
}
.goal-seek__log-table tr:last-child td { border-bottom: none; }
.goal-seek__log-table .col-num { text-align: center !important; width: 28px; }
.goal-seek__log-table .col-muted { color: #8D8F92 !important; font-size: 10px !important; }
.goal-seek__log-table .row--converged td { background: rgba(4, 164, 161, 0.07); color: #0A7479 !important; font-weight: 600; }
.goal-seek__log-table .row--failed td   { background: rgba(209, 30, 29, 0.06);  color: #a51716 !important; font-weight: 600; }

/* ── Gráfico ── */
.goal-seek__chart {
  flex-shrink: 0;
  border-radius: 8px;
  background: #1a1f2e;
  overflow: hidden;
  padding: 4px 4px 0;
}

/* ── Footer ── */
.goal-seek__footer {
  display: flex;
  gap: 10px;
  padding-top: 14px;
  margin-top: 14px;
  border-top: 1px solid #CFD2D2;
  justify-content: flex-end;
}

/* ── Botões ── */
.gs-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s;
}
.gs-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gs-btn--run     { background: #EE7528; color: white; width: 100%; justify-content: center; }
.gs-btn--run:hover:not(:disabled) { background: #d66820; }
.gs-btn--cancel  { background: #D11E1D; color: white; width: 100%; justify-content: center; }
.gs-btn--cancel:hover { background: #a51716; }
.gs-btn--apply   { background: #04A4A1; color: white; }
.gs-btn--apply:hover { background: #0A7479; }
.gs-btn--discard { background: #F4F4F4; color: #545454; border: 1px solid #CFD2D2; }
.gs-btn--discard:hover { background: #e5e5e5; }

/* ── Animação hourglass ── */
.gs-running {
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 4px;
}

@keyframes gs-pulse-rotate {
  0%   { transform: rotate(0deg); opacity: 1; }
  49%  { transform: rotate(0deg); opacity: 0.4; }
  50%  { transform: rotate(180deg); opacity: 0.4; }
  100% { transform: rotate(180deg); opacity: 1; }
}
.gs-spin-slow {
  display: inline-block;
  animation: gs-pulse-rotate 1.4s ease-in-out infinite;
}
</style>
